import { useCallback, useRef, useState } from 'react';
import { signOut } from '../utils/firebase';
import { apiClient } from '../network/ApiClient';

/**
 * Account Deletion (App Store Guideline 5.1.1(v) / Google Play Account
 * Deletion policy) — the Settings danger-zone control's behavior, mirroring
 * useLogout's single-flight/pending contract.
 *
 * Order matters: the backend call (ApiClient#deleteAccount) must succeed
 * BEFORE Firebase signOut() runs, so a failed deletion leaves the caller
 * signed in and able to retry — exactly the confirmed-save discipline every
 * other Settings control in this screen already follows. On success,
 * signOut() fires AuthContext's existing onAuthStateChanged-driven
 * resetRealtimeIdentity() teardown automatically; this hook does not
 * duplicate that teardown, it only triggers it.
 *
 * Deliberately does NOT swallow deleteAccount() errors: this is meant to be
 * passed straight through as a ConfirmContext `confirmRun`, whose own
 * contract is to keep the modal open with an inline error on a rejected
 * promise — the right UX for a destructive, retryable action.
 */
export function useDeleteAccount(onComplete: () => void): { deleting: boolean; deleteAccount: () => Promise<void> } {
  const [deleting, setDeleting] = useState(false);
  const inFlight = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete; // always call the latest callback without re-creating `deleteAccount`

  const deleteAccount = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setDeleting(true);
    try {
      await apiClient.deleteAccount();
      // The account is already gone server-side at this point — a signOut()
      // failure must never re-surface as a "deletion failed" error to the
      // caller (ConfirmContext would otherwise keep the modal open on an
      // account that no longer exists).
      await signOut().catch(() => { /* navigate away regardless */ });
      onCompleteRef.current();
    } finally {
      inFlight.current = false;
      setDeleting(false);
    }
  }, []);

  return { deleting, deleteAccount };
}
